/**
 * A booking in progress (batch-29, item R9; plan §6). One object per event, kept in the tab's session storage under
 * `rmp2.book.<eventId>`, so Back and a reload keep what the guest entered. Only the keys named here are ever written:
 * the card, the access code and every token stay out. A draft whose shape or whose event settings no longer match is
 * dropped. Every read and write is wrapped, so the page works when storage is blocked.
 */
import { plural } from '../../lib/format';
import { addDays } from '../../lib/weeks';
import type { PublicAgeGroup, PublicEvent } from '../public/api';
import { effectiveAgeGroups } from './ageGroups';
import { eventStay, nightCount, stayProblem, stayWindow, type Sold, type StayProblem, type StayRules } from './stayDates';

/** The most people and the most rooms one booking holds unless the event sets its own (owner question B12; item R17). */
export const MAX_PEOPLE = 20;
export const MAX_ROOMS = 10;
/** The limits of one booking. The server is the limit; the pages follow these so a guest is not led past it. */
export interface Limits { rooms: number; people: number }
export const DEFAULT_LIMITS: Limits = { rooms: MAX_ROOMS, people: MAX_PEOPLE };
/** The highest numbers an organizer can set: the most any kept draft may hold. */
export const LIMIT_CEILING: Limits = { rooms: 20, people: 40 };
const wholeFrom1 = (n: unknown, max: number): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= max;
/** The event's own limits; a number that is missing (an older database) or out of range reads as 10 rooms and 20 people. */
export function limitsOf(event: { form?: { max_rooms?: number | null; max_people?: number | null } | null }): Limits {
  const r = event.form?.max_rooms, p = event.form?.max_people;
  const rooms = wholeFrom1(r, LIMIT_CEILING.rooms) ? r : MAX_ROOMS;
  const people = wholeFrom1(p, LIMIT_CEILING.people) ? p : MAX_PEOPLE;
  return { rooms: Math.min(rooms, people), people };
}
const VERSION = 1;

/** One room as the guest filled it (page 2). */
export interface DraftRoom {
  type: string; people: Record<string, number>; cots?: number; cribs?: number;
  /** The room group the guest picked this room from, when the hotel's types are in groups. */
  group?: string;
  /** True while the guest has not named a type of that group: the page gives the room one. */
  any?: boolean;
  /** G3: one place in a shared room, the sharer's gender. Only on a booking of exactly one person; missing = a whole room. */
  share?: 'M' | 'F';
}
/** One extra as the guest chose it (page 3). */
export interface DraftExtra { id: string; people?: Record<string, number>; rooms?: number; qty?: number }
/** One person of the booking (page 4). */
export interface DraftPerson { room: number; group: string; first_name?: string; last_name?: string; /** Only on an event that asks it (./gender). */ gender?: 'M' | 'F' }

export interface Draft {
  v: 1;
  /** The event settings this draft was made under; a draft with another signature is dropped. */
  sig: string;
  /** Null on a fixed-date event: there is nothing to pick. */
  arrive: string | null;
  leave: string | null;
  /** People per age-group key. */
  groups: Record<string, number>;
  roomsWanted: number;
  // filled by the later pages
  hotelId?: string | null;
  rooms?: DraftRoom[];
  /** Cots and cribs the guest took out, by room position. */
  removals?: Array<{ room: number; cots?: number; cribs?: number }>;
  extras?: DraftExtra[];
  names?: DraftPerson[];
  details?: Record<string, string>;
  payDeposit?: boolean;
  promo?: string;
  /** Page 2's list (item PK2): only the room types that can be booked, and the order they are listed in. Missing = every type, lowest price first. */
  availableOnly?: boolean;
  roomSort?: 'price_low' | 'price_high' | 'name';
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
/** The tab's storage, or null where there is none or it is blocked. */
function tabStore(): Store | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}
export const draftKey = (eventId: string): string => `rmp2.book.${eventId}`;

/** The event's date rules. A database without the booking keys sends none: the event's own dates, fixed. */
export function stayRulesOf(event: Pick<PublicEvent, 'stay' | 'start_date' | 'end_date'>): StayRules {
  const s = event.stay;
  if (!s) return { mode: 'fixed', start: event.start_date, end: event.end_date };
  return { mode: s.mode, start: s.start, end: s.end, extraNights: s.extra_nights, minNights: s.min_nights, maxNights: s.max_nights };
}

/** Who can come: the event's age groups, youngest first, or the two built-in ones; and whether every booking needs someone of the last of them. */
export function peopleOf(event: Pick<PublicEvent, 'people'>): { grouped: boolean; groups: PublicAgeGroup[]; adultRequired: boolean } {
  const p = event.people;
  const adultRequired = p?.adult_required === true;
  if (p && Array.isArray(p.groups) && p.groups.length) return { grouped: Boolean(p.grouped), groups: p.groups, adultRequired };
  return { grouped: false, groups: effectiveAgeGroups(null).map((g) => ({ key: g.key, name: g.name, from: g.from, to: g.to, crib: Boolean(g.crib) })), adultRequired };
}

/**
 * Whether a night can be booked: at least one room type that is not closed, at any hotel, has it on sale. A payload
 * that carries no nights at all (an older database) refuses nothing here; the server decides.
 */
export function soldOf(event: Pick<PublicEvent, 'hotels'>): Sold {
  const maps = (event.hotels ?? []).flatMap((h) => h.room_types.filter((t) => !t.closed && t.nights).map((t) => t.nights!));
  if (!maps.length) return () => true;
  return (night) => maps.some((m) => m[night]?.sold === true);
}

/** Changes whenever a setting page 1 depends on changes. */
export function draftSignature(event: Pick<PublicEvent, 'stay' | 'people' | 'start_date' | 'end_date'>): string {
  const r = stayRulesOf(event);
  const p = peopleOf(event);
  return JSON.stringify([VERSION, r.mode, r.start, r.end, r.extraNights ?? null, r.minNights ?? null, r.maxNights ?? null, p.grouped, p.groups.map((g) => [g.key, g.from, g.crib])]);
}

/** A new booking: the event's own stay (as much of it as the longest stay allows), two of the oldest group (one where a booking holds one person), one room. */
export function freshDraft(event: Pick<PublicEvent, 'stay' | 'people' | 'start_date' | 'end_date'> & { form?: PublicEvent['form'] }): Draft {
  const r = stayRulesOf(event);
  const { groups } = peopleOf(event);
  const ev = eventStay(r);
  let stay: { arrive: string | null; leave: string | null } = { arrive: null, leave: null };
  if (r.mode === 'extra') stay = ev;
  else if (r.mode === 'open') {
    const most = r.maxNights != null && r.maxNights >= 1 ? r.maxNights : nightCount(ev.arrive, ev.leave);
    stay = { arrive: ev.arrive, leave: nightCount(ev.arrive, ev.leave) > most ? addDays(ev.arrive, most) : ev.leave };
  }
  const counts: Record<string, number> = {};
  groups.forEach((g, i) => { counts[g.key] = i === groups.length - 1 ? Math.min(2, limitsOf(event).people) : 0; });
  return { v: 1, sig: draftSignature(event), ...stay, groups: counts, roomsWanted: 1 };
}

const isCount = (n: unknown, max: number): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= max;
const isDayOrNull = (v: unknown): v is string | null => v === null || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v));
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The rooms of a draft, each with only the keys a room may hold — or null when any of them is not a room for these age groups. */
export function cleanRooms(raw: unknown, groupKeys: readonly string[], limits: Limits = DEFAULT_LIMITS): DraftRoom[] | null {
  if (!Array.isArray(raw) || raw.length > limits.rooms) return null;
  const out: DraftRoom[] = [];
  for (const r of raw as unknown[]) {
    if (!isRecord(r) || typeof r.type !== 'string' || !r.type || !isRecord(r.people)) return null;
    const people: Record<string, number> = {};
    for (const [k, n] of Object.entries(r.people)) {
      if (!groupKeys.includes(k) || !isCount(n, limits.people)) return null;
      if (n > 0) people[k] = n;
    }
    const room: DraftRoom = { type: r.type, people };
    if (isCount(r.cots, limits.people)) room.cots = r.cots;
    if (isCount(r.cribs, limits.people)) room.cribs = r.cribs;
    if (typeof r.group === 'string' && r.group) { room.group = r.group; if (r.any === true) room.any = true; }
    if (r.share === 'M' || r.share === 'F') room.share = r.share;
    out.push(room);
  }
  return out;
}

const MAX_TEXT = 2000;
/** The longest special-requests text the page takes (and the server keeps). */
export const SPECIAL_REQUESTS_MAX = 1000;
const isText = (v: unknown): v is string => typeof v === 'string' && v.length <= MAX_TEXT;

/** The extras of a draft (page 3), each with only the keys an extra may hold — or null when the list is not one. */
export function cleanExtras(raw: unknown, groupKeys: readonly string[], limits: Limits = DEFAULT_LIMITS): DraftExtra[] | null {
  if (!Array.isArray(raw) || raw.length > 200) return null;
  const out: DraftExtra[] = [];
  for (const x of raw as unknown[]) {
    if (!isRecord(x) || typeof x.id !== 'string' || !x.id) return null;
    const extra: DraftExtra = { id: x.id };
    if (isRecord(x.people)) {
      const people: Record<string, number> = {};
      for (const [k, n] of Object.entries(x.people)) {
        if (!groupKeys.includes(k) || !isCount(n, limits.people)) return null;
        people[k] = n;
      }
      extra.people = people;
    }
    if (isCount(x.rooms, limits.rooms)) extra.rooms = x.rooms;
    if (isCount(x.qty, 99)) extra.qty = x.qty;
    out.push(extra);
  }
  return out;
}

/** The people of a draft (page 4): a room, a group, a first and a last name and a gender — or null when the list is not one. A dietary note an older tab kept is not a person's any more (staleDietary). */
export function cleanNames(raw: unknown, groupKeys: readonly string[], limits: Limits = DEFAULT_LIMITS): DraftPerson[] | null {
  if (!Array.isArray(raw) || raw.length > limits.people) return null;
  const out: DraftPerson[] = [];
  for (const p of raw as unknown[]) {
    if (!isRecord(p) || !isCount(p.room, limits.rooms) || p.room < 1 || typeof p.group !== 'string' || !groupKeys.includes(p.group)) return null;
    const person: DraftPerson = { room: p.room, group: p.group };
    if (isText(p.first_name) && p.first_name) person.first_name = p.first_name;
    if (isText(p.last_name) && p.last_name) person.last_name = p.last_name;
    if (p.gender === 'M' || p.gender === 'F') person.gender = p.gender;
    out.push(person);
  }
  return out;
}

/**
 * The dietary notes a draft kept per person before item BG1 (2026-10-12), one line each — "Noa: no nuts", or "Dietary
 * needs: vegan" for a person with no name on the page — or '' when it has none. They are moved into the special
 * requests, where the guest sees them and can change them, so a note typed before the release is not lost.
 */
export function staleDietary(raw: unknown): string {
  if (!Array.isArray(raw)) return '';
  const lines: string[] = [];
  for (const p of raw as unknown[]) {
    if (!isRecord(p) || !isText(p.dietary) || !p.dietary.trim()) continue;
    const name = isText(p.first_name) ? p.first_name.trim() : '';
    lines.push(`${name || 'Dietary needs'}: ${p.dietary.trim()}`);
  }
  return lines.join('\n');
}

/** What the guest typed on page 4 outside the rooms, by field: text only — or null when it is not that. */
export function cleanDetails(raw: unknown): Record<string, string> | null {
  if (!isRecord(raw)) return null;
  const entries = Object.entries(raw);
  if (entries.length > 100) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of entries) {
    if (!isText(v)) return null;
    if (v) out[k] = v;
  }
  return out;
}

/** Only the keys a draft may hold, each of the right kind — or null when the object is not a draft for these settings. */
export function cleanDraft(raw: unknown, sig: string, groupKeys: readonly string[], limits: Limits = DEFAULT_LIMITS): Draft | null {
  if (!isRecord(raw) || raw.v !== VERSION || raw.sig !== sig) return null;
  if (!isDayOrNull(raw.arrive) || !isDayOrNull(raw.leave) || !isRecord(raw.groups)) return null;
  const groups: Record<string, number> = {};
  for (const k of groupKeys) {
    const n = raw.groups[k] ?? 0;
    if (!isCount(n, limits.people)) return null;
    groups[k] = n;
  }
  if (Object.keys(raw.groups).some((k) => !groupKeys.includes(k))) return null;
  if (Object.values(groups).reduce((a, b) => a + b, 0) > limits.people) return null;
  if (!isCount(raw.roomsWanted, limits.rooms) || raw.roomsWanted < 1) return null;
  const out: Draft = { v: 1, sig, arrive: raw.arrive, leave: raw.leave, groups, roomsWanted: raw.roomsWanted };
  if (typeof raw.hotelId === 'string' || raw.hotelId === null) out.hotelId = raw.hotelId;
  const rooms = cleanRooms(raw.rooms, groupKeys, limits);
  if (rooms) out.rooms = rooms;
  if (Array.isArray(raw.removals)) out.removals = raw.removals as NonNullable<Draft['removals']>;
  const extras = cleanExtras(raw.extras, groupKeys, limits);
  if (extras) out.extras = extras;
  const names = cleanNames(raw.names, groupKeys, limits);
  if (names) out.names = names;
  const details = cleanDetails(raw.details);
  if (details) out.details = details;
  const notes = staleDietary(raw.names);
  if (notes) {
    const was = out.details?.special_requests ?? '';
    // held to the field's own length, so the draft stays one the page keeps
    out.details = { ...out.details, special_requests: (was ? `${was}\n${notes}` : notes).slice(0, SPECIAL_REQUESTS_MAX) };
  }
  if (typeof raw.payDeposit === 'boolean') out.payDeposit = raw.payDeposit;
  if (typeof raw.promo === 'string') out.promo = raw.promo;
  if (raw.availableOnly === true) out.availableOnly = true;
  if (raw.roomSort === 'price_low' || raw.roomSort === 'price_high' || raw.roomSort === 'name') out.roomSort = raw.roomSort;
  return withShareCleared(out);
}

/** A place in a shared room is for a party of exactly one person (G3): any other party's rooms lose it. A draft with no shared room comes back as it was. */
export function withShareCleared<T extends Pick<Draft, 'groups' | 'rooms'>>(d: T): T {
  if (!d.rooms?.some((r) => r.share) || totalPeople(d) === 1) return d;
  return { ...d, rooms: d.rooms.map((r) => { const { share, ...room } = r; void share; return room; }) };
}

/** The saved draft for this event, or null. One that no longer matches — or holds more than the event's limits now allow — is removed. */
export function loadDraft(event: Pick<PublicEvent, 'id' | 'stay' | 'people' | 'start_date' | 'end_date'> & { form?: PublicEvent['form'] }, store: Store | null = tabStore()): Draft | null {
  if (!store) return null;
  try {
    const raw = store.getItem(draftKey(event.id));
    if (raw == null) return null;
    let parsed: unknown = null;
    try { parsed = JSON.parse(raw); } catch { /* not a draft */ }
    const draft = cleanDraft(parsed, draftSignature(event), peopleOf(event).groups.map((g) => g.key), limitsOf(event));
    if (!draft) store.removeItem(draftKey(event.id));
    return draft;
  } catch {
    return null;
  }
}

/** Keep the draft in the tab. False when it could not be kept (the page carries on from memory). */
export function saveDraft(eventId: string, draft: Draft, store: Store | null = tabStore()): boolean {
  if (!store) return false;
  try {
    // a shape check only: the pages already kept the draft inside the event's limits, which are read again on load
    const clean = cleanDraft(draft, draft.sig, Object.keys(draft.groups), LIMIT_CEILING);
    if (!clean) return false;
    store.setItem(draftKey(eventId), JSON.stringify(clean));
    return true;
  } catch {
    return false;
  }
}

/** Forget the draft: after a booking is made. */
export function clearDraft(eventId: string, store: Store | null = tabStore()): void {
  try {
    store?.removeItem(draftKey(eventId));
  } catch {
    /* nothing was kept */
  }
}

export const totalPeople = (d: Pick<Draft, 'groups'>): number => Object.values(d.groups).reduce((a, b) => a + b, 0);
/** Everyone who is not in the group that sleeps in cribs. */
export function notBabies(d: Pick<Draft, 'groups'>, groups: readonly PublicAgeGroup[]): number {
  return groups.reduce((n, g) => n + (g.crib ? 0 : d.groups[g.key] ?? 0), 0);
}
/** The most rooms this party may ask for: one each for the people who are not babies, the event's limit at most (10 unless set), never under 1. */
export const mostRooms = (d: Pick<Draft, 'groups'>, groups: readonly PublicAgeGroup[], limits: Limits = DEFAULT_LIMITS): number =>
  Math.max(1, Math.min(limits.rooms, notBabies(d, groups)));

/** A group's count changed. The party never passes the event's limit (20 unless set), and the rooms asked for follow it down. */
export function withGroupCount(d: Draft, groups: readonly PublicAgeGroup[], key: string, n: number, limits: Limits = DEFAULT_LIMITS): Draft {
  const others = totalPeople(d) - (d.groups[key] ?? 0);
  const count = Math.max(0, Math.min(Math.trunc(n) || 0, limits.people - others));
  const next = { ...d, groups: { ...d.groups, [key]: count } };
  return withShareCleared({ ...next, roomsWanted: Math.max(1, Math.min(next.roomsWanted, mostRooms(next, groups, limits))) });
}
export function withRooms(d: Draft, groups: readonly PublicAgeGroup[], n: number, limits: Limits = DEFAULT_LIMITS): Draft {
  return { ...d, roomsWanted: Math.max(1, Math.min(Math.trunc(n) || 1, mostRooms(d, groups, limits))) };
}

/** The stay this draft asks for: its own dates, or the event's on a fixed-date event. */
export function stayOf(rules: StayRules, d: Pick<Draft, 'arrive' | 'leave'>): { arrive: string; leave: string } {
  if (rules.mode === 'fixed') return eventStay(rules);
  return { arrive: d.arrive ?? '', leave: d.leave ?? '' };
}

/** The shortest and the longest stay of an open stretch, in nights. */
export function nightLimits(rules: StayRules): { min: number; max: number } {
  const w = stayWindow(rules);
  const whole = Math.max(1, nightCount(w.first, w.last));
  const max = rules.maxNights != null && rules.maxNights >= 1 ? Math.min(rules.maxNights, whole) : whole;
  return { min: Math.min(Math.max(1, rules.minNights ?? 1), max), max };
}

export type WhoProblem = StayProblem | 'nobody' | 'no_adult';
/**
 * Why page 1 cannot go on, or null when it can. The dates are checked first, then the party. With `adultRequired` (the
 * event's rule, migration 0122) the party must hold someone of the last group, the oldest, as the server checks it.
 */
export function whoProblem(rules: StayRules, groups: readonly PublicAgeGroup[], d: Draft, sold: Sold, adultRequired = false): WhoProblem | null {
  if (rules.mode !== 'fixed') {
    const s = stayOf(rules, d);
    const p = stayProblem(rules, s.arrive, s.leave, sold);
    if (p) return p;
  }
  if (notBabies(d, groups) < 1) return 'nobody';
  const oldest = groups[groups.length - 1];
  return adultRequired && oldest && (d.groups[oldest.key] ?? 0) < 1 ? 'no_adult' : null;
}

/** The server's sentence for a booking with nobody of the oldest group (booking_adult_message, migration 0122). */
export const adultMessage = (groups: readonly PublicAgeGroup[]): string =>
  `Every booking needs at least one person aged ${groups[groups.length - 1]?.from ?? 18} or over.`;

/** The one line page 1 shows for a problem. Dates outside what the event allows read as a night that is not available. */
export function whoMessage(problem: WhoProblem, rules: StayRules, groups: readonly PublicAgeGroup[] = []): string {
  if (problem === 'nobody') return 'Add at least one adult or child.';
  if (problem === 'no_adult') return adultMessage(groups);
  if (problem === 'stay_too_short') return `Stays here are at least ${plural(nightLimits(rules).min, 'night')}.`;
  if (problem === 'stay_too_long') return `Stays here are at most ${plural(nightLimits(rules).max, 'night')}.`;
  return "That night isn't available.";
}
export const CODE_MESSAGE = "That code isn't right. Please check it.";

/** True when everything page 1 asks for is in order: the later pages open only then. */
export const whoDone = (rules: StayRules, groups: readonly PublicAgeGroup[], d: Draft, sold: Sold, adultRequired = false): boolean =>
  whoProblem(rules, groups, d, sold, adultRequired) === null;

/** The last day an open-stretch arrival may be: the day before the window ends. */
export const lastArrival = (rules: StayRules): string => addDays(stayWindow(rules).last, -1);
